import { useEffect, useState } from "react";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSButton from "views/system-design/DSButton";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import { WatchCollection, WatchMode } from "../../../api/admin";
import { useCreateClockFieldWatch } from "../hooks/useClockFieldWatches";

interface CreateWatchModalProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_THRESHOLD = 10;

const CreateWatchModal = ({ open, onClose }: CreateWatchModalProps) => {
  const [mode, setMode] = useState<WatchMode>(WatchMode.HeartbeatOnline);
  const [clockId, setClockId] = useState("");
  const [collectionName, setCollectionName] = useState<WatchCollection>(
    WatchCollection.MachinesReport,
  );
  const [fieldPath, setFieldPath] = useState("heartbeat");
  const [targetValue, setTargetValue] = useState("");
  const [thresholdMinutes, setThresholdMinutes] = useState(DEFAULT_THRESHOLD);
  const [notifyTarget, setNotifyTarget] = useState("");

  const { mutate: create, isPending: isCreating } = useCreateClockFieldWatch();

  const isHeartbeat = mode === WatchMode.HeartbeatOnline;

  useEffect(() => {
    if (open) {
      setMode(WatchMode.HeartbeatOnline);
      setClockId("");
      setCollectionName(WatchCollection.MachinesReport);
      setFieldPath("heartbeat");
      setTargetValue("");
      setThresholdMinutes(DEFAULT_THRESHOLD);
      setNotifyTarget("");
    }
  }, [open]);

  // Heartbeat-online is always the machinesReport.heartbeat edge; lock those.
  useEffect(() => {
    if (isHeartbeat) {
      setCollectionName(WatchCollection.MachinesReport);
      setFieldPath("heartbeat");
    }
  }, [isHeartbeat]);

  const trimmedClockId = clockId.trim();
  const trimmedField = fieldPath.trim();

  const handleCreate = () => {
    const trimmedTarget = targetValue.trim();
    create(
      {
        clockId: trimmedClockId,
        collectionName,
        fieldPath: trimmedField,
        mode,
        thresholdMinutes,
        notifyTarget: notifyTarget.trim() || null,
        targetValue: isHeartbeat || trimmedTarget === "" ? null : trimmedTarget,
      },
      { onSuccess: onClose },
    );
  };

  const canCreate =
    !isCreating &&
    trimmedClockId.length > 0 &&
    trimmedField.length > 0 &&
    thresholdMinutes > 0;

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>New watcher</DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Mode
            </DSTypography>
            <DSSegmentedTabs
              size="small"
              value={mode}
              onChange={(value) =>
                setMode((value as WatchMode) ?? WatchMode.HeartbeatOnline)
              }
              options={[
                { label: "Heartbeat online", value: WatchMode.HeartbeatOnline },
                { label: "Field change", value: WatchMode.Change },
              ]}
            />
          </DSBox>

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography variant="caption" color="text.secondary">
              Clock ID
            </DSTypography>
            <DSTextField
              size="small"
              value={clockId}
              placeholder="device id (Firestore doc id)"
              onChange={(e) => setClockId(e.target.value)}
            />
          </DSBox>

          {isHeartbeat ? (
            <DSTypography variant="caption" color="text.secondary">
              Notifies when the clock comes back online — a fresh
              machinesReport.heartbeat after a gap larger than the threshold.
            </DSTypography>
          ) : (
            <>
              <DSBox
                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                <DSTypography variant="caption" color="text.secondary">
                  Collection
                </DSTypography>
                <DSEnumSelect
                  size="small"
                  value={collectionName}
                  options={[
                    {
                      value: WatchCollection.MachinesReport,
                      label: "machinesReport",
                    },
                    { value: WatchCollection.Machines, label: "machines" },
                  ]}
                  onChange={setCollectionName}
                />
              </DSBox>

              <DSTextField
                label="Field path"
                size="small"
                value={fieldPath}
                placeholder="e.g. version or remoteBattery.value"
                onChange={(e) => setFieldPath(e.target.value)}
              />

              <DSBox
                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
              >
                <DSTextField
                  label="Target value (optional)"
                  size="small"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                />
                <DSTypography variant="caption" color="text.secondary">
                  Leave empty to alert on any change. Set a value to alert only
                  when the field changes to exactly that value.
                </DSTypography>
              </DSBox>
            </>
          )}

          <DSTextField
            label={
              isHeartbeat
                ? "Offline threshold (minutes)"
                : "Dedup window (minutes)"
            }
            type="number"
            size="small"
            value={thresholdMinutes}
            onChange={(e) => setThresholdMinutes(Number(e.target.value))}
          />

          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTextField
              label="Slack webhook override (optional)"
              size="small"
              value={notifyTarget}
              onChange={(e) => setNotifyTarget(e.target.value)}
            />
            <DSTypography variant="caption" color="text.secondary">
              Leave empty to use the default alerts webhook.
            </DSTypography>
          </DSBox>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <DSButton size="small" onClick={onClose}>
          Cancel
        </DSButton>
        <DSButton
          variant="contained"
          size="small"
          disabled={!canCreate}
          onClick={handleCreate}
        >
          Create
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CreateWatchModal;
